import React from 'react';
import {
  Box,
  Container,
  Typography,
  Paper,
  Grid,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import {
  Update,
  Work,
  ArrowBack,
  Article,
  CheckCircle,
  AttachMoney,
  Business,
  TrendingUp,
  Assignment,
  CompareArrows,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';

const SkilledWorkerUpdates = () => {
  const theme = useTheme();

  const keyUpdates = [
    {
      title: 'Salary Requirements',
      icon: AttachMoney,
      details: [
        'New minimum salary threshold of £38,700',
        'Health and education sector exceptions',
        'Experience-based salary requirements',
      ],
    },
    {
      title: 'Eligible Occupations',
      icon: Business,
      details: [
        'Updated Shortage Occupation List',
        'New skill level requirements',
        'Industry-specific criteria',
      ],
    },
    {
      title: 'Points System',
      icon: TrendingUp,
      details: [
        'Revised points calculation',
        'Additional points categories',
        'Tradeable points options',
      ],
    },
    {
      title: 'Visa Duration',
      icon: Assignment,
      details: [
        'Extended visa validity periods',
        'Renewal process changes',
        'Settlement pathway updates',
      ],
    },
  ];

  const salaryThresholds = [
    {
      occupation: 'General Skilled Workers',
      previous: '£26,200',
      new: '£38,700',
      notes: 'Standard minimum threshold',
    },
    {
      occupation: 'Healthcare Workers',
      previous: '£26,200',
      new: '£29,000',
      notes: 'Special consideration for NHS roles',
    },
    {
      occupation: 'Education Professionals',
      previous: '£26,200',
      new: '£34,000',
      notes: 'Modified for teaching roles',
    },
    {
      occupation: 'Technology Specialists',
      previous: '£26,200',
      new: '£38,700',
      notes: 'Aligned with market rates',
    },
  ];

  return (
    <Box sx={{ py: { xs: 6, md: 8 } }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ mb: 6 }}>
          <Button
            component={RouterLink}
            to="/"
            startIcon={<ArrowBack />}
            sx={{ mb: 3 }}
          >
            Back to Home
          </Button>
          <Typography
            variant="h1"
            sx={{
              fontSize: { xs: '2rem', md: '3rem' },
              fontWeight: 700,
              mb: 2,
              background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`,
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}
          >
            Skilled Worker Visa Updates
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mb: 3 }}>
            <Chip
              icon={<Update />}
              label="March 5, 2024"
              sx={{ backgroundColor: `${theme.palette.primary.main}15` }}
            />
            <Chip
              icon={<Work />}
              label="Work Visa"
              sx={{ backgroundColor: `${theme.palette.secondary.main}15` }}
            />
          </Box>
          <Typography
            variant="h5"
            color="textSecondary"
            sx={{ mb: 4, maxWidth: 800 }}
          >
            Recent changes to the Skilled Worker visa category and salary requirements, affecting both employers and applicants.
          </Typography>
          <Divider sx={{ my: 4 }} />
        </Box>

        {/* Main Content */}
        <Grid container spacing={4}>
          {/* Key Updates */}
          {keyUpdates.map((update, index) => (
            <Grid item xs={12} md={6} key={index}>
              <Paper
                elevation={2}
                sx={{
                  p: 3,
                  height: '100%',
                  transition: 'transform 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                  },
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <update.icon
                    sx={{
                      fontSize: 40,
                      color: theme.palette.primary.main,
                      mr: 2,
                    }}
                  />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    {update.title}
                  </Typography>
                </Box>
                <List>
                  {update.details.map((detail, idx) => (
                    <ListItem key={idx} sx={{ px: 0 }}>
                      <ListItemIcon>
                        <CheckCircle color="primary" sx={{ fontSize: 20 }} />
                      </ListItemIcon>
                      <ListItemText primary={detail} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            </Grid>
          ))}
        </Grid>

        {/* Salary Thresholds Table */}
        <Paper sx={{ mt: 6, p: 4 }}>
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              fontWeight: 600,
              mb: 4,
              color: theme.palette.primary.main,
            }}
          >
            New Salary Thresholds
          </Typography>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Occupation Category</TableCell>
                  <TableCell>Previous Threshold</TableCell>
                  <TableCell>New Threshold</TableCell>
                  <TableCell>Notes</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {salaryThresholds.map((row) => (
                  <TableRow key={row.occupation}>
                    <TableCell component="th" scope="row">
                      {row.occupation}
                    </TableCell>
                    <TableCell>{row.previous}</TableCell>
                    <TableCell>{row.new}</TableCell>
                    <TableCell>{row.notes}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        {/* Impact Section */}
        <Paper
          sx={{
            mt: 6,
            p: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.light}15)`,
            borderRadius: 2,
          }}
        >
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" gutterBottom>
                Impact on Employers
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CompareArrows color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Review and adjust salary packages" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CompareArrows color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Update recruitment strategies" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CompareArrows color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Reassess sponsorship licenses" />
                </ListItem>
              </List>
            </Grid>
            <Grid item xs={12} md={6}>
              <Typography variant="h5" gutterBottom>
                Impact on Applicants
              </Typography>
              <List>
                <ListItem>
                  <ListItemIcon>
                    <CompareArrows color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Higher salary requirements" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CompareArrows color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="New points calculation" />
                </ListItem>
                <ListItem>
                  <ListItemIcon>
                    <CompareArrows color="primary" />
                  </ListItemIcon>
                  <ListItemText primary="Modified eligibility criteria" />
                </ListItem>
              </List>
            </Grid>
          </Grid>
        </Paper>

        {/* Call to Action */}
        <Paper
          sx={{
            mt: 6,
            p: 4,
            background: `linear-gradient(135deg, ${theme.palette.primary.main}15, ${theme.palette.primary.light}15)`,
            borderRadius: 2,
          }}
        >
          <Typography variant="h5" gutterBottom>
            Need Help with Your Skilled Worker Visa Application?
          </Typography>
          <Typography paragraph color="textSecondary">
            Our immigration experts can guide you through the new requirements and help ensure your application meets all criteria.
          </Typography>
          <Button
            component={RouterLink}
            to="/consultation"
            variant="contained"
            color="primary"
            size="large"
            sx={{ mt: 2 }}
          >
            Schedule a Consultation
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default SkilledWorkerUpdates; 